import type { SharedValue } from "react-native-reanimated";
import { Easing, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { StyleSheet } from "react-native-unistyles";

import { EPG_SURFACE_OPACITY } from "$src/core/constants/epg-grid";
import useAnimatedTheme from "$src/core/hooks/use-animated-theme";
import opacity from "$src/core/utils/color/opacity";
import CustomPlatform from "$src/core/utils/platform/platform";

const SUPPORTS_OUTLINE_PROPS = CustomPlatform.select<boolean>({
  android: false,
  androidTV: false,
  default: true,
});

const ACTIVE_OPACITY = 0.32;

const stylesheet = StyleSheet.create(({ colors, spacing, border }) => ({
  item: {
    borderRadius: border.radius.lg,
    alignItems: "center",
    justifyContent: "center",
    minHeight: CustomPlatform.select({
      tv: spacing(8),
      default: spacing(12),
    }),
    paddingVertical: spacing(2),
    paddingHorizontal: spacing(3),
    // TV keeps the CMS surface; phone/web use the subtle Figma tint over the page.
    backgroundColor: CustomPlatform.select({
      tv: colors.backgroundSurface,
      default: opacity(colors.foregroundDefault, EPG_SURFACE_OPACITY),
    }),
    overflow: "hidden",
    _web: {
      _focus: {
        outline: "none",
      },
      "_focus-visible": {
        outlineStyle: "solid",
        outlineColor: colors.backgroundFocus,
        outlineWidth: border.widths.md,
        outlineOffset: border.widths.sm,
        borderRadius: border.radius.lg,
      },
    },
  },
  active: {
    // TV keeps a translucent pill so selected never looks like D-pad focus.
    backgroundColor: CustomPlatform.select({
      tv: opacity(colors.foregroundDefault, ACTIVE_OPACITY),
      default: colors.backgroundFocus,
    }),
  },
  hoverable: {
    _web: {
      transition: "background-color 0.2s ease-in-out",
      _hover: {
        backgroundColor: colors.backgroundFloat,
      },
    },
  },
}));

export const useAnimatedStyles = ({
  isFocused,
}: {
  isFocused: SharedValue<boolean>;
}) => {
  const animatedTheme = useAnimatedTheme();

  const itemStyle = useAnimatedStyle(() => {
    if (!SUPPORTS_OUTLINE_PROPS) return {};

    const { borderFocus } = animatedTheme.value.colors;

    return {
      outlineWidth: animatedTheme.value.border.widths.md,
      outlineColor: withTiming(isFocused.value ? borderFocus : "transparent", {
        duration: 150,
        easing: Easing.out(Easing.ease),
      }),
    };
  });

  return {
    itemStyle,
  };
};

export default stylesheet;
